/**
 * Pure derivations behind the DR plan list (#1421 P3b).
 *
 * Kept out of the components because these are the judgements the surface
 * exists to make — "is this plan's verification still worth anything" and
 * "did the verification actually pass" — and they are worth asserting
 * directly rather than through rendered markup.
 */
import type { RoutingDrPlanRecord, RoutingPreflightSummary } from '@nadoutong/contracts';
import { parseTimestampMs } from '../../components/formatters.ts';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Verification age buckets.
 *
 * `never` is its own bucket rather than "infinitely stale" because it calls
 * for a different action: a plan that was never verified has no result to
 * re-read, so the operator runs the first verification instead of deciding
 * whether an old one still holds.
 *
 * `unreadable` is separated from `never` for the same reason one step further
 * out: the plan may well carry a stored verification result, but nothing can
 * be said about *when* it was taken, so neither the age nor the verdict beside
 * it can be trusted. Folding it into `never` would render a confident "Passed"
 * next to "Never verified", which is a contradiction the operator has to
 * resolve instead of the surface.
 *
 * The thresholds are deliberately coarse. The dangerous plan is the one
 * verified "some time ago" against supply that has since changed, and no
 * threshold makes that precise — what the list owes the operator is that the
 * difference between "yesterday" and "five weeks ago" is visible without
 * subtracting two timestamps in their head.
 */
export type DrPlanFreshness = 'never' | 'unreadable' | 'fresh' | 'aging' | 'stale';

/** Verified within this window: recent enough to act on without re-running. */
export const DR_PLAN_FRESH_DAYS = 7;
/** Past this, the verification is treated as expired rather than merely old. */
export const DR_PLAN_STALE_DAYS = 30;

export interface DrPlanVerificationAge {
  readonly freshness: DrPlanFreshness;
  /** Whole days since the verification; null when never verified. */
  readonly days: number | null;
  /** Whole hours since the verification; null when never verified. */
  readonly hours: number | null;
}

export function drPlanVerificationAge(
  lastVerifiedAt: string | null,
  nowMs: number,
): DrPlanVerificationAge {
  if (lastVerifiedAt === null || lastVerifiedAt.length === 0) {
    return { freshness: 'never', days: null, hours: null };
  }
  const verifiedMs = parseTimestampMs(lastVerifiedAt);
  if (!Number.isFinite(verifiedMs)) {
    // An unparseable timestamp is not evidence of a recent verification, and
    // reporting it as fresh would be the one failure mode this column exists
    // to prevent. It is also not the same as never having verified — see
    // {@link DrPlanFreshness}.
    return { freshness: 'unreadable', days: null, hours: null };
  }
  // A clock skew that puts the verification slightly in the future must not
  // read as a negative age; clamp at zero and treat it as just verified.
  const elapsedMs = Math.max(0, nowMs - verifiedMs);
  const days = Math.floor(elapsedMs / DAY_MS);
  const hours = Math.floor(elapsedMs / HOUR_MS);
  const freshness: DrPlanFreshness =
    days < DR_PLAN_FRESH_DAYS ? 'fresh' : days < DR_PLAN_STALE_DAYS ? 'aging' : 'stale';
  return { freshness, days, hours };
}

/**
 * What the stored verification concluded.
 *
 * `blocking` — the plan would not fully serve traffic: SKUs resolve to
 *   nothing, or targets carry blocking findings. Switching to it is a known
 *   outage for part of the fleet.
 * `caution` — it serves, but not from the plan itself (fall-through) or not
 *   at full health (degraded targets).
 * `clean` — nothing found.
 */
export type DrPlanVerdict = 'clean' | 'caution' | 'blocking';

export function drPlanVerdict(summary: RoutingPreflightSummary): DrPlanVerdict {
  if (summary.uncovered > 0 || summary.unhealthy_targets > 0) return 'blocking';
  if (summary.fall_through > 0 || summary.degraded_targets > 0) return 'caution';
  return 'clean';
}

/**
 * Plan list ordering: operator-assigned rank first, because that is the order
 * the runbook names them in. Ties break on label so the list does not shuffle
 * between renders, and finally on config id so it is total.
 */
export function compareDrPlans(a: RoutingDrPlanRecord, b: RoutingDrPlanRecord): number {
  if (a.plan_rank !== b.plan_rank) return a.plan_rank - b.plan_rank;
  const byLabel = a.label.localeCompare(b.label);
  return byLabel !== 0 ? byLabel : a.config_id.localeCompare(b.config_id);
}
